import React, { useState, useEffect, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, X, Plus, Stethoscope } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface DiagnosisCode {
  id: string;
  code: string;
  description: string;
  description_zh: string | null;
  category: string;
  subcategory: string | null;
}

interface DiagnosisCodeSelectorProps {
  selectedIcdCodes: string[];
  selectedCptCodes: string[];
  onIcdCodesChange: (codes: string[]) => void;
  onCptCodesChange: (codes: string[]) => void;
  language?: 'en' | 'zh';
}

export function DiagnosisCodeSelector({
  selectedIcdCodes,
  selectedCptCodes,
  onIcdCodesChange,
  onCptCodesChange,
  language = 'en',
}: DiagnosisCodeSelectorProps) {
  const [codes, setCodes] = useState<DiagnosisCode[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCodes = async () => {
      const { data, error } = await supabase
        .from('diagnosis_codes' as any)
        .select('*')
        .eq('is_active', true)
        .order('subcategory')
        .order('code');

      if (!error && data) {
        setCodes(data as unknown as DiagnosisCode[]);
      }
      setIsLoading(false);
    };
    fetchCodes();
  }, []);

  const icdCodes = useMemo(() => codes.filter(c => c.category === 'icd10'), [codes]);
  const cptCodes = useMemo(() => codes.filter(c => c.category === 'cpt'), [codes]);

  const filterCodes = (list: DiagnosisCode[]) => {
    if (!searchTerm) return list;
    const term = searchTerm.toLowerCase();
    return list.filter(c =>
      c.code.toLowerCase().includes(term) ||
      c.description.toLowerCase().includes(term) ||
      (c.description_zh && c.description_zh.includes(term)) ||
      (c.subcategory && c.subcategory.toLowerCase().includes(term))
    );
  };

  const toggleCode = (code: string, category: 'icd10' | 'cpt') => {
    if (category === 'icd10') {
      onIcdCodesChange(
        selectedIcdCodes.includes(code)
          ? selectedIcdCodes.filter(c => c !== code)
          : [...selectedIcdCodes, code]
      );
    } else {
      onCptCodesChange(
        selectedCptCodes.includes(code)
          ? selectedCptCodes.filter(c => c !== code)
          : [...selectedCptCodes, code]
      );
    }
  };

  const renderCodeList = (list: DiagnosisCode[], selected: string[], category: 'icd10' | 'cpt') => {
    const filtered = filterCodes(list);
    const grouped = filtered.reduce<Record<string, DiagnosisCode[]>>((acc, code) => {
      const group = code.subcategory || 'Other';
      if (!acc[group]) acc[group] = [];
      acc[group].push(code);
      return acc;
    }, {});

    return (
      <ScrollArea className="h-[280px]">
        <div className="space-y-3 pr-3">
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{group}</p>
              <div className="space-y-1">
                {items.map(item => {
                  const isSelected = selected.includes(item.code);
                  return (
                    <button
                      key={item.id}
                      onClick={() => toggleCode(item.code, category)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between gap-2 ${
                        isSelected
                          ? 'bg-primary/10 border border-primary/30 text-primary'
                          : 'hover:bg-muted/50 border border-transparent'
                      }`}
                    >
                      <div className="min-w-0">
                        <span className="font-mono font-semibold text-xs">{item.code}</span>
                        <span className="ml-2 text-xs">
                          {language === 'zh' && item.description_zh ? item.description_zh : item.description}
                        </span>
                      </div>
                      {isSelected ? (
                        <X className="h-3.5 w-3.5 flex-shrink-0" />
                      ) : (
                        <Plus className="h-3.5 w-3.5 flex-shrink-0 opacity-0 group-hover:opacity-100" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {Object.keys(grouped).length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">No codes found</p>
          )}
        </div>
      </ScrollArea>
    );
  };

  if (isLoading) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Stethoscope className="h-4 w-4" />
          Diagnosis & Procedure Codes
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Selected codes display */}
        {(selectedIcdCodes.length > 0 || selectedCptCodes.length > 0) && (
          <div className="flex flex-wrap gap-1.5">
            {selectedIcdCodes.map(code => (
              <Badge key={code} variant="default" className="gap-1 text-xs cursor-pointer" onClick={() => toggleCode(code, 'icd10')}>
                ICD: {code}
                <X className="h-3 w-3" />
              </Badge>
            ))}
            {selectedCptCodes.map(code => (
              <Badge key={code} variant="secondary" className="gap-1 text-xs cursor-pointer" onClick={() => toggleCode(code, 'cpt')}>
                CPT: {code}
                <X className="h-3 w-3" />
              </Badge>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search codes..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9 h-9"
          />
        </div>

        {/* Code tabs */}
        <Tabs defaultValue="icd10">
          <TabsList className="grid w-full grid-cols-2 h-8">
            <TabsTrigger value="icd10" className="text-xs">ICD-10 Diagnosis</TabsTrigger>
            <TabsTrigger value="cpt" className="text-xs">CPT Procedure</TabsTrigger>
          </TabsList>
          <TabsContent value="icd10" className="mt-2">
            {renderCodeList(icdCodes, selectedIcdCodes, 'icd10')}
          </TabsContent>
          <TabsContent value="cpt" className="mt-2">
            {renderCodeList(cptCodes, selectedCptCodes, 'cpt')}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
